import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { MeResponse } from '@swiftpay/shared';
import { authApi } from './api.js';

type AuthState =
  | { status: 'loading' }
  | { status: 'anon' }
  | { status: 'authed'; user: MeResponse };

/** Единый признак «пользователь — клиент» (учитывает множественные роли). */
export function isClientUser(u: { role: string; roles?: string[] | undefined }): boolean {
  return u.role === 'client' || (u.roles ?? []).includes('client');
}

const Ctx = createContext<{
  state: AuthState;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}>({ state: { status: 'loading' }, refresh: async () => {}, logout: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = async () => {
    try {
      const u = await authApi.me();
      setState({ status: 'authed', user: u });
    } catch {
      setState({ status: 'anon' });
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // даже если API не ответил — локально становимся анонимными
    }
    setState({ status: 'anon' });
  };

  return <Ctx.Provider value={{ state, refresh, logout }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
